<template>
  <div class="data-source-block">
    <div class="bordered">
      <ValidationObserver
        ref="validationObserver"
        v-slot="{ handleSubmit }"
      >
        <form
          class="contact-info"
          novalidate="novalidate"
          @submit.prevent="handleSubmit(submit)"
          @input="handleFormChange"
        >
          <div class="wrapper main-form-container inner-bordered">
            <div
              v-if="hasChanges && shouldShowWarning"
              class="warning-message"
            >
              <inline-svg
                :src="iconWarning"
                class="warning-icon"
              ></inline-svg>
              Changes won’t be saved unless you click Apply.
            </div>
            <div class="title">
              {{ configurationsText }}
            </div>
            <div class="column column-1">
              <div
                v-if="formData.id"
                style="margin-bottom: 27px"
              >
                <FoldersManagement
                  :key="`FoldersManagement${formData.id}-${formData.isInited}`"
                  :selectedValue="formData.mediaFolder"
                  v-bind="formFields.mediaFolder"
                  :label="'Source Folder'"
                  :required="true"
                  :commonError="foldError"
                  :onChange="onFoldersChange"
                />
              </div>
              <div
                v-if="collectionType === 'feature'"
                style="margin-bottom: 27px"
              >
                <MediaItemsManagement
                  label="Select Feature Media"
                  :selectedFolder="formData.mediaFolder"
                  :selectedValue="formData.mediaItem"
                  :required="true"
                  :commonError="mediaItemError"
                  :onChange="onFeatureItemChange"
                />
              </div>
              <MaterialSelect
                v-model="formData.sortBy"
                v-bind="formFields.sortBy"
                :options="sortByOptions"
                :withEmptyField="false"
                :handleChange="changeSortBy"
              />
              <MaterialSelect
                v-model="formData.sortDirection"
                v-bind="formFields.sortDirection"
                :options="sortOrderOptions"
                :withEmptyField="false"
                :handleChange="changeSortOrder"
              />
              <MediaTagManagement
                v-if="collectionType !== 'feature'
                  && isReadyTodisplayIncludeMediaTypes && typesOptions.length"
                :key="`includedMediaTypes${formData.id}-${formData.isInited}`"
                :selectedValue="formData.includeMediaTypes"
                label="Include Media Types"
                :tagPlaceholder="''"
                :searchable="false"
                :onChange="onTagIncludedTypesChange"
                :customOptions="typesOptions"
                :isAudio="true"
                @onClick="handleClickOnIncludeMediaTypes"
              />
              <MediaTagManagement
                v-if="collectionType !== 'feature' && contentTypesOptions.length"
                :key="`includedContentTypes${formData.id}-${formData.isInited}`"
                :selectedValue="formData.includeContentTypes"
                label="Include Content Types"
                :tagPlaceholder="''"
                :searchable="false"
                :onChange="onTagContentTypesChange"
                :customOptions="contentTypesOptions"
                :isAudio="true"
                @onClick="handleClickOnIncludeContentTypes"
              />
              <div class="toggle-wrapper">
                <div class="toggle-title">
                  Sync with Source Folder
                </div>
                <ToggleButton
                  v-model="formData.isSyncWithFolder"
                  color="#2591CF"
                  :sync="true"
                  :labels="false"
                  :disabled="true"
                />
              </div>
            </div>
            <div class="action-buttons-wrapper">
              <Button
                title="Revert"
                classType="warning"
                className="without-margin"
                :eventFunction="handleRevertForm"
              />
              <Button
                title="Apply"
                classType="primary"
                type="submit"
                className="without-margin"
              />
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
    <div class="bordered">
      <div class="feeds-preview">
        <FeedResultsPreview
          :key="getKeyResults"
          :isNeedFixedItems="false"
          :mediaItems="mediaItems"
          :getMediaItems="getMediaItems"
          :isDynamicContent="true"
          :pagination="pagination.total"
          :dynamicContentLimit="formData.dynamicContentLimit"
          :defaultLength="defaultLength"
          :errorLiveEvents="errorLiveEvents"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  SET_EDIT_FORM,
} from '../../store/actions/common';
import {
  GET_CONTENT_TYPE_REQUEST,
  GET_MEDIA_TYPE_REQUEST,
  GET_SORT_BY_REQUEST,
  GET_SORT_ORDER_REQUEST,
  GET_DYNAMIC_MEDIA_REQUEST,
} from '../../store/actions/playlist/playlist';

import {
  GET_FEATURE_SORT_BY_REQUEST,
  GET_FEATURE_SORT_ORDER_REQUEST,
} from '../../store/actions/features/features';

import FoldersManagement from '../FoldersManagement.vue';
import MaterialSelect from '../inputs/MaterialSelect.vue';
import MediaTagManagement from '../media/MediaTagManagement.vue';
import FeedResultsPreview from '../feed/FeedResultsPreview.vue';
import MediaItemsManagement from '../MediaItemsManagement.vue';
import Button from '../common/Button.vue';
import BaseForm from './BaseForm.vue';
import iconWarning from '../../assets/icons/icon-warning-localization.svg';

export default {
  name: 'CollectionDataSourceForm',
  components: {
    Button,
    FeedResultsPreview,
    MediaTagManagement,
    MaterialSelect,
    FoldersManagement,
    MediaItemsManagement,
  },
  extends: BaseForm,
  props: {
    formPrefix: {
      type: String,
      default: 'playlistEditForm',
    },
    metadata: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
    submitEventName: {
      type: String,
      required: true,
    },
    submitParams: {
      type: Object,
      default: () => ({}),
    },
    successMessageText: {
      type: String,
      default: '',
    },
    collectionType: {
      type: String,
      default: '',
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    setHasChanges: {
      type: Function,
      default: () => {},
    },
    shouldShowWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconWarning,
      submitEvent: this.submitEventName,
      successMessage: this.successMessageText,
      defaultLength: 20,
      folderKey: 0,
      sortByKey: 0,
      sortOrderKey: 0,
      isReadyTodisplayIncludeMediaTypes: false,
      mediaItems: null,
      foldError: '',
      errorLiveEvents: [],
      mediaItemError: '',
      startEditIncludeMediaTypes: false,
      startEditContentTypes: false,
      formData: {
        id: '',
        sortBy: 'episodic',
        sortDirection: 'ASC',
        includeMediaTypes: [],
        includeContentTypes: [],
        isSyncWithFolder: true,
        mediaFolder: '',
        mediaItem: null,
        dynamicContentLimit: 100,
        isInited: false,
      },
      formFields: {
        sortBy: {
          type: 'text',
          name: 'sortBy',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
          },
        },
        sortDirection: {
          type: 'text',
          name: 'sortDirection',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
          },
        },
      },
      errorMapping: {
        mediaFolder: [
          `${this.formPrefix}.mediaFolder`,
        ],
      },
    };
  },
  computed: {
    ...mapState({
      pagination: (state) => state.feeds.mediaItemsPagination,
      featuresSortByOptions: (state) => state.features.sortByOptions,
      playlistSortByOptions: (state) => state.playlist.sortByOptions,
      featuresSortOrderOptions: (state) => {
        const options = state.features.sortOrderOptions;
        return options.map((item) => ({
          code: item.code,
          name: item.name === 'ASC' ? 'Ascending' : 'Descending',
        }));
      },
      playlistSortOrderOptions: (state) => {
        const options = state.playlist.sortOrderOptions;
        return options.map((item) => ({
          code: item.code,
          name: item.name === 'ASC' ? 'Ascending' : 'Descending',
        }));
      },
      typesOptions: (state) => state.playlist.typesOptions,
      contentTypesOptions: (state) => state.playlist.contentTypesOptions,
    }),
    sortByOptions() {
      const options = this.collectionType === 'feature' ? this.featuresSortByOptions : this.playlistSortByOptions;
      return options;
    },
    sortOrderOptions() {
      const options = this.collectionType === 'feature' ? this.featuresSortOrderOptions : this.playlistSortOrderOptions;
      return options;
    },
    getKeyResults() {
      const {
        mediaFolder,
        includeMediaTypes,
        includeContentTypes,
        sortBy,
        sortDirection,
      } = this.formData;

      return mediaFolder
        + includeMediaTypes.length
        + includeContentTypes.length
        + sortBy
        + sortDirection
        + this.sortOrderKey
        + this.sortByKey
        + this.folderKey;
    },
    configurationsText() {
      let type = 'Playlist';

      if (this.type === 'season') {
        type = 'Season';
      } else if (this.type === 'feature') {
        type = 'Feature';
      }

      return `${type} Configurations`;
    },
  },
  watch: {
    typesOptions: {
      handler(newVal) {
        if (newVal.length) {
          this.prepareIncludeMediaTypes();
        }
      },
      deep: true,
    },
    formData: {
      handler(newVal) {
        if (newVal.includeMediaTypes && newVal.isInited) {
          this.prepareIncludeMediaTypes();
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.collectionType === 'feature') {
      this.$store.dispatch(GET_FEATURE_SORT_BY_REQUEST);
      this.$store.dispatch(GET_FEATURE_SORT_ORDER_REQUEST);
    } else {
      this.$store.dispatch(GET_SORT_BY_REQUEST);
      this.$store.dispatch(GET_SORT_ORDER_REQUEST);
      this.$store.dispatch(GET_MEDIA_TYPE_REQUEST);
      this.$store.dispatch(GET_CONTENT_TYPE_REQUEST);
    }
    this.initFormData();
    this.getMediaItems(1);
  },
  methods: {
    initFormData(isRevert = false) {
      if (this.metadata) {
        this.formData.id = this.metadata.id;
        this.formData.mediaFolder = this.metadata.mediaFolder ? this.metadata.mediaFolder.id : null;
        this.formData.sortBy = this.metadata.sortBy;
        this.formData.sortDirection = this.metadata.sortDirection;
        if (this.collectionType !== 'feature') {
          this.formData.includeMediaTypes = this.metadata.includeMediaTypes.map((item) => ({
            id: item,
            name: item,
          }));
          this.formData.includeContentTypes = this.metadata.includeContentTypes.map((item) => ({
            id: item,
            name: item,
          }));

          if (isRevert) {
            this.isReadyTodisplayIncludeMediaTypes = false;
            this.prepareIncludeMediaTypes();
          }
        } else {
          this.formData.mediaItem = this.metadata.mediaItem ? this.metadata.mediaItem.id : null;
        }

        this.$store.dispatch(SET_EDIT_FORM, false);
        this.setHasChanges(false);
        this.formData.isInited = true;
      }
    },
    getMediaItems(page) {
      if (!this.formData.mediaFolder) {
        this.mediaItems = [];
        return;
      }

      const allLength = page * this.defaultLength;
      const { dynamicContentLimit } = this.formData;
      let folders = this.formData.mediaFolder;
      let { includeMediaTypes } = this.formData;
      let { includeContentTypes } = this.formData;

      if (this.formData.mediaFolder[0] && this.formData.mediaFolder[0].name) {
        folders = this.formData.mediaFolder.map((item) => item.id);
      }

      if (this.formData.includeMediaTypes[0] && this.formData.includeMediaTypes[0].name) {
        includeMediaTypes = this.formData.includeMediaTypes.map((item) => item.id);
      }

      if (this.formData.includeContentTypes[0] && this.formData.includeContentTypes[0].name) {
        includeContentTypes = this.formData.includeContentTypes.map((item) => item.id);
      }

      const params = {
        allLength: dynamicContentLimit < allLength
          ? dynamicContentLimit : allLength,
        accountId: this.getCurrentAccountId,
        page: 1,
        mediaFolder: folders,
        includeMediaTypes,
        includeContentTypes,
        sortBy: this.formData.sortBy,
        sortDirection: this.formData.sortDirection,
        checkAvailability: 1,
        isHideChildren: true,
      };
      this.$store.dispatch(GET_DYNAMIC_MEDIA_REQUEST, params)
        .then((res) => {
          this.mediaItems = res;
        });
    },
    prepareIncludeMediaTypes() {
      if (
        this.formData.includeMediaTypes
        && this.formData.isInited
        && this.typesOptions.length
        && !this.isReadyTodisplayIncludeMediaTypes
      ) {
        this.formData.includeMediaTypes.forEach((item) => {
          const type = this.typesOptions.find((i) => i.code === item.id);
          if (type) {
            item.name = type.name;
          }
        });

        this.isReadyTodisplayIncludeMediaTypes = true;
      }
    },
    clearFolderError() {
      this.foldError = '';
      // this.tabErrors.dataSource = false;
    },
    onTagIncludedTypesChange(value) {
      if (this.startEditIncludeMediaTypes) {
        this.handleFormChange();
      }

      this.formData.includeMediaTypes = value;
      this.getMediaItems(1);
    },
    onTagContentTypesChange(value) {
      if (this.startEditContentTypes) {
        this.handleFormChange();
      }

      this.formData.includeContentTypes = value;
      this.getMediaItems(1);
    },
    handleClickOnIncludeMediaTypes() {
      if (!this.startEditIncludeMediaTypes) {
        this.startEditIncludeMediaTypes = true;
      }
    },
    handleClickOnIncludeContentTypes() {
      if (!this.startEditContentTypes) {
        this.startEditContentTypes = true;
      }
    },
    clearMediaItemError() {
      this.mediaItemError = '';
    },
    onFeatureItemChange(value) {
      this.handleFormChange();
      this.formData.mediaItem = value ? value.id : value;
      this.clearMediaItemError();
    },
    onFoldersChange(value) {
      this.handleFormChange();
      this.formData.mediaFolder = value ? value.code : value;
      this.folderKey += 1;
      this.clearFolderError();
      this.getMediaItems(1);
    },
    changeSortBy() {
      this.handleFormChange();
      this.sortByKey += 1;
      this.getMediaItems(1);
    },
    changeSortOrder() {
      this.handleFormChange();
      this.sortOrderKey += 1;
      this.getMediaItems(1);
    },
    handleFormChange() {
      if (this.formData.isInited) {
        this.$store.dispatch(SET_EDIT_FORM, true);
        this.setHasChanges(true);
      }
    },
    handleRevertForm() {
      this.$store.dispatch(SET_EDIT_FORM, false);
      this.formData.isInited = false;
      this.$emit('resetShouldShowWarning');

      this.$nextTick(() => {
        this.initFormData(true);
      });
    },
    getRequestData() {
      const { submitParams } = this;

      const data = {
        mediaFolder: this.formData.mediaFolder,
        sortBy: this.formData.sortBy,
        sortDirection: this.formData.sortDirection,
      };

      if (this.collectionType === 'feature') {
        data.mediaItem = this.formData.mediaItem;
      } else {
        data.includeMediaTypes = this.formData.includeMediaTypes;
        data.includeContentTypes = this.formData.includeContentTypes;
      }

      submitParams.data = data;

      return submitParams;
    },
    onSubmitFail(error) {
      const folderError = error.form_errors
        && error.form_errors.children
        && error.form_errors.children.mediaFolder
        && error.form_errors.children.mediaFolder.errors
        && error.form_errors.children.mediaFolder.errors[0].error;

      if (folderError) {
        // this.tabErrors.dataSource = true;
      }

      if (error.form_errors.children && error.form_errors.children.title) {
        // this.tabErrors.general = true;
      }

      this.formErrors = this.getServerFormErrors(error);
      this.itemsErrors = this.getServerFormCollectionErrors(error);

      if (folderError && folderError === 'NOT_SYNCHRONIZED_ERROR') {
        this.foldError = 'This folder is already in use';
        return;
      }

      if (folderError && folderError === 'IS_BLANK_ERROR') {
        this.foldError = 'This field is required';
        return;
      }

      if (Object.entries(this.formErrors).length && this.$refs[this.validationObserver]) {
        this.$refs[this.validationObserver].setErrors(this.formErrors);
      } else {
        this.$toasted.global.error({
          message: this.errorMessage,
        });
      }
    },
    onSubmitSuccess(res) {
      this.$toasted.global.success({
        message: this.successMessage,
      });

      if (res.data?.id === this.metadata.id) {
        this.$emit('handleUpdatedMetadata', res.data);
      } else {
        this.$emit('reloadItem');
      }
      this.$store.dispatch(SET_EDIT_FORM, false);
      this.setHasChanges(false);
      this.$emit('resetShouldShowWarning');
    },
  },
};
</script>

<style scoped>
.data-source-block {
  display: flex;
}

.bordered {
  border: 1px solid #383b40;
  height: calc(100vh - 270px);
  margin-right: 30px;
  width: 100%;
}

.inner-bordered {
  height: calc(100vh - 310px);
  padding: 20px;
  overflow-x: hidden;
  overflow-y: auto;
}

.bordered:last-child {
  margin-right: 0;
}

.toggle-wrapper {
  align-items: center;
  display: flex;
  margin-right: 30px;
  width: 17vw;
  margin-top: 30px;
}

.toggle-title {
  margin-right: 10px;
}
.action-buttons-wrapper {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-top: 20px;
}
.warning-message {
  color: #F2C94C;
  gap: 6px;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid #F2C94C;
  border-radius: 4px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  width: fit-content;
  margin-bottom: 30px;
}
</style>
